import { Box, Paper, Stack, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useTranslation } from 'react-i18next'
import { IconTicket } from '@tabler/icons-react'
import type { Price } from 'api/dto/price'
import type { RegisteredVisitor } from '../types'
import { SectionHeader } from './section-header'
import { TicketCard } from './ticket-card'
import { AddonCard } from './addon-card'
import { QuantitySelector } from './quantity-selector'

interface TicketSelectionSectionProps {
  form: UseFormReturnType<RegisteredVisitor, (values: RegisteredVisitor) => RegisteredVisitor>
  isSmallDevice: boolean
  prices: Price[]
  addons: Price[]
  companions: Price[]
  selectedPrice: Price | undefined
  onMainAddonToggle: (addonId: string, isCompanion?: boolean) => void
  onQuantityChange: (numTickets: number) => void
}

export function TicketSelectionSection({ form, isSmallDevice, prices, addons, companions, selectedPrice, onMainAddonToggle, onQuantityChange }: TicketSelectionSectionProps) {
  const { t } = useTranslation()

  // Show error if no prices available
  if (!selectedPrice && prices.length === 0) {
    return (
      <Paper p="xl" radius="md" sx={{ background: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
        <Text color="red" weight={500}>
          {t('register.no-price-available')}
        </Text>
      </Paper>
    )
  }

  if (prices.length === 0) {
    return null
  }

  return (
    <Paper p={isSmallDevice ? 'md' : 'xl'} radius="md" sx={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <SectionHeader icon={<IconTicket size={16} />} title={t('register.your-ticket')} />

      <Stack spacing={12}>
        {prices.map((price) => (
          <TicketCard key={price.id} price={price} isSelected={form.values.price === price.id} onSelect={() => form.setFieldValue('price', price.id)} t={t} />
        ))}
      </Stack>

      {/* Main attendee addons */}
      {addons.length > 0 && (
        <Box mt={20}>
          <Text size="sm" weight={600} color="#374151" mb={10}>
            {t('register.addons')}
          </Text>
          <Stack spacing={8}>
            {addons.map((addon) => (
              <AddonCard key={addon.id} addon={addon} isSelected={form.values.addons?.includes(addon.id) || false} onToggle={() => onMainAddonToggle(addon.id)} t={t} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Companions for main attendee */}
      {companions.length > 0 && (
        <Box mt={16}>
          <Stack spacing={8}>
            {companions.map((companionPrice) => (
              <AddonCard
                key={companionPrice.id}
                addon={companionPrice}
                isSelected={form.values.addons?.includes(companionPrice.id) || false}
                onToggle={() => onMainAddonToggle(companionPrice.id, true)}
                t={t}
              />
            ))}
          </Stack>
        </Box>
      )}

      {selectedPrice && (
        <Box mt={20} pt={16} sx={{ borderTop: '1px solid #e5e7eb' }}>
          <QuantitySelector value={form.values.numberOfTickets || 1} onChange={onQuantityChange} min={1} max={10} label={t('register.number-of-tickets')} />
        </Box>
      )}
    </Paper>
  )
}
